import type { LucideIcon } from 'lucide-react'
import {
  Download,
  FileCheck2,
  FileSpreadsheet,
  FileText,
  LineChart,
  Presentation,
  QrCode,
  Sparkles,
  Users,
} from 'lucide-react'
import { CREATORS_ENABLED } from './creators/featureFlag'
import artifactCover from './assets/artifact-cover.png'
import pdfCover from './assets/pdf-cover.png'
import youtubeCover from './assets/youtube-cover.png'
import leojCover from './assets/leoj-cover.png'
import qrCover from './assets/qr-cover.png'
import salesforceCover from './assets/salesforce-cover.png'
import confirmationCover from './assets/confirmation-cover.png'
import townhallCover from './assets/townhall-cover.png'

export type AppStatus = 'active' | 'coming-soon'

/**
 * Per-app access gate. Currently every app is open to every signed-in
 * @leferi.com user — kept as a function so we can wire app-specific
 * allowlists back in later (e.g. for a future closed beta) without
 * rewiring every caller.
 */
export function isAppGatedForUser(_app: AppEntry, _email: string): boolean {
  return false
}

/**
 * App targets are one of:
 *  - `url`: an external destination (a different subdomain, e.g. artifact.leferi.com)
 *  - `to` : an internal SPA path (e.g. /youtubedownloader) handled by react-router
 *
 * Cards use a regular `<a>` for url targets (cross-origin navigation) and
 * a `<Link>` for to targets (client-side route push).
 */
export interface AppEntry {
  id: string
  name: string
  description: string
  icon: LucideIcon
  url?: string
  to?: string
  status: AppStatus
  featured?: boolean
  /**
   * Per-app cover imagery. Each app's cover is that app's *icon* on the
   * landing page — the chrome (eyebrow / LIVE chip / headline / footer)
   * stays consistent across all featured cards, so the cover image is
   * what makes one app distinguishable from another at a glance.
   * Mirrors the LCMD SNS look-and-feel system (preview 28): consistent
   * type grid overlaid on per-post imagery.
   */
  cover?: string
  /**
   * Tailwind background class used as a stand-in cover when no `cover`
   * image is supplied yet — renders the editorial cover-variant card with
   * a brand gradient instead of a photo. Drop in `cover` later to swap.
   */
  coverFallback?: string
  /** 전사 직원(portalAccess.employee)에게만 보이고 열리는 앱 — "for Leferist" 탭에 놓인다.
   *  탭 노출(canLeferist)과 RequireEmployee 라우트 게이트가 같은 값을 본다. */
  employeeOnly?: boolean
}

/**
 * Top-level landing tabs. "for Everyone" holds the company-wide tools;
 * "for Labels & Creator Solutions" holds per-label tooling (organized by label
 * sub-tabs); "for Brand Intelligence Consulting" holds BIC tooling (Creators).
 * "for Leferist" holds the staff-only apps (APPS 중 employeeOnly).
 * Visibility is gated per-user by the admin (see /api/me/access) — bic 는 현재
 * everyone 과 동일 게이팅(별도 권한 설계 전).
 */
export type PortalTab = 'everyone' | 'labels' | 'bic' | 'leferist'

/** Canonical labels under the "Labels & Creator Solutions" tab. Keys must
 *  match the server's PORTAL_LABEL_KEYS (admin.ts). */
export interface PortalLabel {
  key: string
  name: string
}
export const PORTAL_LABELS: PortalLabel[] = [
  { key: 'daead', name: 'DAEAD' },
  { key: 'caape', name: 'Caape!' },
  { key: 'lbe', name: 'LBE' },
  { key: 'ledinary', name: 'Le.dinary' },
  { key: 'thenextlabel', name: 'The Next Label' },
]

/**
 * Apps shown under each label sub-tab in "for Labels & Creator Solutions".
 * Empty array → the label renders its "준비 중" empty state. The Leo J
 * dashboard is a server-rendered page (gated by its own whitelist), so it
 * uses `url` for a full-page navigation rather than a SPA `to` route.
 */
export const LABEL_APPS: Record<string, AppEntry[]> = {}

/**
 * 레이블별 "Creator Channel Dashboards" 카드 이미지.
 * ⬇⬇⬇ 대표님: 각 레이블 카드에 쓸 실제 이미지 URL을 여기에 채워 넣으세요.
 *   - 값을 채우면 그 이미지가 카드 배경으로 렌더됩니다.
 *   - 비워두면('') 아래 LABEL_CARD_FALLBACK의 레이블별 그라디언트가 표시됩니다.
 *   - import한 에셋(예: `import xCover from './assets/x.png'`) 또는 외부 URL 모두 가능.
 * 키는 PORTAL_LABELS의 key와 동일해야 합니다(daead/caape/lbe/ledinary/thenextlabel).
 */
export const LABEL_CARD_IMAGE: Record<string, string> = {
  daead: leojCover, // ← DAEAD 카드 이미지 (기본: 레오제이 커버). 교체 가능.
  caape: '', // ← Caape! 카드 이미지 URL을 넣으세요
  lbe: '', // ← LBE 카드 이미지 URL을 넣으세요
  ledinary: '', // ← Le.dinary 카드 이미지 URL을 넣으세요
  thenextlabel: '', // ← The Next Label 카드 이미지 URL을 넣으세요
}

/** 이미지 URL이 없을 때 쓰는 레이블별 그라디언트(placeholder) — 5개 레이블이
 *  서로 다르게 보이도록. 실제 이미지를 LABEL_CARD_IMAGE에 넣으면 그게 우선합니다. */
// Top color varies per label; the bottom stays the ink brown-black (#352628)
// — "그라데이션 블랙 부분 유지". Custom (non-palette) colors use Tailwind
// arbitrary gradients so the design-system palette stays untouched.
const LABEL_CARD_FALLBACK: Record<string, string> = {
  daead: 'bg-grad-blue-brown', // 파랑 → 블랙 (기존)
  caape: 'bg-[linear-gradient(180deg,#9C1A5C_50%,#352628_100%)]', // 짙은 자주색 → 블랙
  lbe: 'bg-grad-blue-grey', // (변경 요청 없음, 기존)
  ledinary: 'bg-[linear-gradient(180deg,#8B9A7C_50%,#352628_100%)]', // Le.dinary 로고 세이지 그린 → 블랙
  thenextlabel: 'bg-[linear-gradient(180deg,#5A2A86_50%,#352628_100%)]', // 짙은 보라색 → 블랙
}

/**
 * Single "Creator Channel Dashboards" card per label. Clicking it opens the
 * label's creator grid (/labels/:label/creators) — the unified entry point
 * (replaces per-creator cards). SPA `to` route; the grid then links out to
 * each creator's server-rendered /dashboard/:handle. Image differs per label
 * via LABEL_CARD_IMAGE (real URL) → LABEL_CARD_FALLBACK (gradient placeholder).
 */
export function creatorDashboardsCard(labelKey: string): AppEntry {
  const img = LABEL_CARD_IMAGE[labelKey]
  return {
    id: `creator-dashboards-${labelKey}`,
    name: 'Creator Channel Dashboards',
    description: '레이블 소속 크리에이터 채널 성장 · Hub Program 분석 대시보드.',
    icon: LineChart,
    to: `/labels/${labelKey}/creators`,
    status: 'active',
    cover: img || undefined,
    coverFallback: LABEL_CARD_FALLBACK[labelKey] || 'bg-grad-blue-brown',
  }
}

export const APPS: AppEntry[] = [
  {
    id: 'artifact',
    name: 'Artifacts',
    description: 'Share Claude-generated HTML as links instead of pasted text.',
    icon: Sparkles,
    url: 'https://artifact.leferi.com',
    status: 'active',
    featured: true,
    cover: artifactCover,
  },
  {
    id: 'youtube-downloader',
    name: 'YouTube Downloader',
    description: 'Download YouTube videos for internal use.',
    icon: Download,
    to: '/youtubedownloader',
    status: 'active',
    cover: youtubeCover,
  },
  {
    id: 'pdf-compressor',
    name: 'PDF Compressor',
    description: 'Shrink PDFs to fit AI upload caps without losing layout.',
    icon: FileText,
    to: '/pdfcompressor',
    status: 'active',
    cover: pdfCover,
  },
  {
    // Salesforce 기회 상품(OpportunityLineItem) 대량 업로드 — 포털이 Data Loader용
    // 엑셀을 만들어 주고, 사람이 그 파일을 Salesforce Data Loader로 올린다.
    id: 'salesforce-products',
    name: 'Salesforce Products',
    description: '기회 상품(OpportunityLineItem)을 Data Loader로 올릴 엑셀을 만들어요.',
    icon: FileSpreadsheet,
    to: '/salesforce-products',
    status: 'active',
    employeeOnly: true,
    cover: salesforceCover,
  },
  {
    // 확인서 발송 — SF 확인서(Quote) 조회 → 템플릿으로 PDF 생성·SF 저장 →
    // 크리에이터별 메일 초안 → 확인 후 일괄 발송(SMTP, cr_lecommend 발신). 발송 이력은 포털 DB.
    id: 'confirmation-docs',
    name: '확인서 발송',
    description: '확인서(Quote)로 PDF 생성 → 크리에이터별 메일 초안 → 일괄 발송.',
    icon: FileCheck2,
    to: '/confirmations',
    status: 'active',
    employeeOnly: true,
    cover: confirmationCover,
  },
  {
    // Townhall — 매주 월요일 타운홀 자료를 연도별로 쌓는 히스토리. 파일(S3) 또는 웹 링크.
    id: 'townhall',
    name: 'Townhall',
    description: '매주 월요일 타운홀 자료를 연도별로 모아봐요.',
    icon: Presentation,
    to: '/townhall',
    status: 'active',
    employeeOnly: true,
    cover: townhallCover,
  },
  {
    // QR 코드 생성 — 링크를 넣으면 브라우저에서 즉시 QR 생성(외부 전송 없음), PNG·SVG 다운로드.
    id: 'qr-generator',
    name: 'QR Generator',
    description: '링크를 넣으면 자동으로 QR 코드를 만들어요. PNG·SVG 다운로드.',
    icon: QrCode,
    to: '/qr',
    status: 'active',
    cover: qrCover,
  },
]

/** Apps under the "for Leferist" tab — 전사 직원 전용(portalAccess.employee). */
export const LEFERIST_APPS: AppEntry[] = APPS.filter((app) => app.employeeOnly)

/**
 * Apps under the "for Brand Intelligence Consulting" tab. Currently just the
 * Creators workspace, feature-flagged (CREATORS_ENABLED = 라우트/카드가 존재하는가).
 * 탭 노출은 레이블과 같은 규칙 — 권한(portalAccess.bic)만 본다(App.tsx canBic).
 * Moved out of "for Everyone" per the 시안's top-level category structure.
 */
export const BIC_APPS: AppEntry[] = CREATORS_ENABLED
  ? [
      {
        id: 'creators',
        name: 'Creators',
        description: 'Browse and match creators to brand campaigns.',
        icon: Users,
        to: '/creators',
        status: 'active',
        coverFallback: 'bg-grad-blue-brown',
      },
    ]
  : []
